import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { StatGrid } from "@/components/StatGrid";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { OrganisationForm } from "@/modules/organisations/OrganisationForm";
import { useOrgUserBreakdown, useOrganisations, type Organisation } from "@/modules/organisations/fixtures";
import { SoonButton } from "@/components/SoonButton";

/** Domain vocabulary stays in the module (U6) — StatusChip only renders it. */
const ORG_STATUS: StatusMap = {
  active: { label: "ACTIVE", color: "var(--ok)" },
  inactive: { label: "INACTIVE", color: "var(--t-mute)" },
};


/** Module-level so the identity is stable across renders — DataTable memoizes
 *  rows against it, and a fresh array each render defeats that. */
const COLS: DataCol<Organisation>[] = [
  { key: "name", label: "Organisation", width: 190,
    render: (o) => <Link to={`/organisations/organisations/${o.organisation_id}`}>{o.name}</Link> },
  { key: "id", label: "ID", width: 110, mono: true, render: (o) => o.organisation_id },
  { key: "desc", label: "Description", width: 230,
    render: (o) => o.description ?? <span className="cap">—</span> },
  {
    key: "ent", label: "Entitlements", width: 230,
    title: (o) => o.entitlements.map((e) => e.plan ? `${e.product} ${e.plan}` : e.product).join(", "),
    render: (o) => o.entitlements.length === 0
      ? <span className="cap">directory only</span>
      : o.entitlements.map((e) => e.plan ? `${e.product} ${e.plan}` : e.product).join(" · "),
  },
  { key: "users", label: "Users", width: 70, render: (o) => o.users || "—" },
  { key: "status", label: "Status", width: 96, render: (o) => <StatusChip st={o.status} map={ORG_STATUS} /> },
];

/** Organisations: every party Orbit knows.
 *
 *  An organisation becomes a CUSTOMER by holding an entitlement — derived from
 *  `entitlements`, never a stored flag, because a boolean beside the thing it
 *  describes is a column that can disagree with it (§9c).
 *
 *  A DIRECTORY-ONLY org — a cell vendor named in a BOM — has no users, no
 *  access and costs nothing. It exists so the catalogue can name its maker
 *  without inventing a second identity for them.
 */
export function OrganisationsPage() {
  const orgs = useOrganisations();
  const [search, setSearch] = useState("");
  const [access, setAccess] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const [mode, setMode] = useState<"idle" | "new">("idle");
  const [done, setDone] = useState("");

  const counts = useMemo(() => ({
    all: orgs.length,
    allocated: orgs.filter((o) => o.entitlements.length > 0).length,
    nousers: orgs.filter((o) => o.users === 0).length,
    inactive: orgs.filter((o) => o.status === "inactive").length,
  }), [orgs]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => {
      if (q && !o.name.toLowerCase().includes(q) && !o.organisation_id.toLowerCase().includes(q)) return false;
      if (access === "allocated" && o.entitlements.length === 0) return false;
      if (access === "none" && o.entitlements.length > 0) return false;
      if (access === "inactive" && o.status !== "inactive") return false;
      return true;
    });
  }, [orgs, search, access]);

  const org = orgs.find((o) => o.organisation_id === sel) ?? null;
  const breakdown = useOrgUserBreakdown(org?.organisation_id ?? "");

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Organisations"
        title="Organisations"
        desc="Companies on the platform. An organisation is a name that can have users; products are allocated to it separately."
        right={mode === "idle"
          ? <button className="btn teal" onClick={() => { setMode("new"); setDone(""); }}>Add organisation</button>
          : <button className="btn ghost" onClick={() => setMode("idle")}>← Back</button>}
      />

      {mode === "new" && (
        <div className="panel panel-pad" style={{ marginBottom: 16 }}>
          <div className="section-label">New organisation</div>
          <OrganisationForm
            onCancel={() => setMode("idle")}
            onDone={(name) => { setDone(`${name} created`); setMode("idle"); }}
          />
        </div>
      )}
      {done && <div style={{ marginBottom: 10, fontSize: 12.5, color: "var(--ok)" }}>{done}</div>}

      <StatGrid stats={[
        { k: "Organisations", v: counts.all },
        { k: "With products", v: counts.allocated, s: "allocated something" },
        { k: "No users yet", v: counts.nousers },
        { k: "Inactive", v: counts.inactive },
      ]} />

      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SearchInput placeholder="Search name / ID…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
        <Select value={access} onChange={(e) => setAccess(e.target.value)}>
          <option value="">All organisations</option>
          <option value="allocated">With products</option>
          <option value="none">No products yet</option>
          <option value="inactive">Inactive</option>
        </Select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>
          {rows.length} of {orgs.length}
        </span>
      </div>

      <DataTable
        rows={rows}
        cols={COLS}
        rowKey={(o) => o.organisation_id}
        stickyFirst
        pageSize={25}
        onRowClick={(o) => setSel(o.organisation_id)}
        empty={<div className="panel panel-pad cap">No organisations match these filters.</div>}
      />

      {/* Click the ROW to peek; click the NAME to go to the full page. Two
          different questions — "what is this?" and "I need to work on it" —
          and making the second the only option means a long trip for the first. */}
      {org && (
        <Drawer onClose={() => setSel(null)} width="min(520px,96vw)"
          header={<>
            <span style={{ fontWeight: 700 }}>{org.name}</span>
            <span className="mono cap">{org.organisation_id}</span>
            <StatusChip st={org.status} map={ORG_STATUS} />
          </>}>
          <div className="section-label">About</div>
          <KV k="Description" v={org.description ?? "—"} mono={false} />
          <KV k="Created" v={org.created_at} />
          <KV k="Created by" v={org.created_by} mono={false} />

          <div className="section-label tight">People</div>
          <KV k="Company admins" v={breakdown.admins} mono={false} />
          <KV k="Company users" v={breakdown.users} mono={false} />
          <KV k="Total" v={breakdown.total} mono={false} />
          <div className="cap" style={{ marginTop: 6 }}>
            An organisation must keep at least one active admin — the last one cannot be removed or demoted.
          </div>

          <div className="section-label tight">Products</div>
          {org.entitlements.length === 0
            ? <div className="cap">Nothing allocated. Its people cannot sign in to anything yet.</div>
            : org.entitlements.map((e) => (
                <KV key={e.product} k={e.product} v={e.plan ?? "allocated"} mono={false} />
              ))}

          <div className="section-label tight">Manage</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Link className="btn teal sm" to={`/organisations/organisations/${org.organisation_id}`}>
              Open organisation
            </Link>
            <Link className="btn ghost sm" to="/organisations/users">View its people</Link>
          </div>

          <div className="section-label tight">Danger</div>
          <SoonButton className="btn ghost sm">
            {org.status === "active" ? "Deactivate" : "Reactivate"}
          </SoonButton>
          <div className="cap" style={{ marginTop: 6 }}>
            Deactivating blocks every one of its people from signing in. Nothing is deleted, and it is reversible.
          </div>
        </Drawer>
      )}
    </div>
  );
}
